import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store.ts'
import { Button } from '@radix-ui/themes'
import {
  fetchConditionalAccessStatus,
  fetchDevicePolicies,
  fetchDevicePolicy,
  fetchMTDStatus,
  selectSettings,
} from '../../reducers/settings/index.ts'
import { useSelector } from 'react-redux'
import { IconListCheck, IconLoader2 } from '@tabler/icons-react'
import DataGrid from '../data-grid/DataGrid.tsx'
import { policyColumns } from './PolicyColumns.tsx'
import { inPov } from '../../utils/session.ts'
import { selectUser } from '../../reducers/users/users.slice.ts'
import { useNavigate } from 'react-router-dom'
import { fetchAllTravelPolicies } from '../../reducers/travel/travel.thunk.ts'
import { selectTravel } from '../../reducers/travel/travel.slice.ts'
import { DevicePolicy } from '../../types/SettingsType.ts'
import RenamePolicyModal from './RenamePolicyModal.tsx'
import DeletePolicyModal from './DeletePolicyModal.tsx'
import DuplicatePolicyModal from './DuplicatePolicyModal.tsx'

function NewDevicePoliciesWrapper() {
  const di = useAppDispatch()
  const navigate = useNavigate()
  const { devicePolicies, mtdStatus } = useSelector(selectSettings)
  const { travelPolicies } = useSelector(selectTravel)
  const { session } = useSelector(selectUser)
  const [policiesLoading, setPoliciesLoading] = useState<boolean>(false)
  const [showRenamePolicyModal, setShowRenamePolicyModal] = useState<boolean>(false)
  const [showDeletePolicyModal, setShowDeletePolicyModal] = useState<boolean>(false)
  const [showDuplicatePolicyModal, setShowDuplicatePolicyModal] = useState<boolean>(false)
  const [combinedPolicies, setCombinedPolicies] = useState<DevicePolicy[]>([])
  const [policyToAction, setPolicyToAction] = useState<DevicePolicy | undefined>(undefined)
  const [isThreatLevelEnabled, setIsThreatLevelEnabled] = useState<boolean>(false)

  useEffect(() => {
    setPoliciesLoading(true)
    di(fetchDevicePolicies())
    di(fetchAllTravelPolicies())
    di(fetchConditionalAccessStatus())
    di(fetchMTDStatus())
  }, [])

  useEffect(() => {
    if (devicePolicies && travelPolicies) {
      setPoliciesLoading(false)

      const combined = devicePolicies.map((devicePolicy) => {
        const matchingTravelPolicy = travelPolicies.find(
          (travelPolicy) => travelPolicy.devicePolicyId === devicePolicy.policyId
        )

        return {
          ...devicePolicy,
          travelEnabled: matchingTravelPolicy ? 'true' : 'false',
        }
      })

      setCombinedPolicies(combined)
    }
  }, [devicePolicies, travelPolicies])

  useEffect(() => {
    setIsThreatLevelEnabled(mtdStatus !== undefined ? mtdStatus : false)
  }, [mtdStatus])

  const goToDevicePolicy = (policyId: number) => {
    navigate(`/device-policies/${policyId}`)
  }

  const renamePolicy = async (policyId: number) => {
    setShowRenamePolicyModal(true)
    const policy = await di(fetchDevicePolicy(policyId))
    setPolicyToAction(policy)
  }

  const duplicatePolicy = async (policyId: number) => {
    setShowDuplicatePolicyModal(true)
    const policy = await di(fetchDevicePolicy(policyId))
    setPolicyToAction(policy)
  }

  const deletePolicy = async (policyId: number) => {
    setShowDeletePolicyModal(true)
    const policy = await di(fetchDevicePolicy(policyId))
    setPolicyToAction(policy)
  }

  return (
    <>
      <div className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l bg-contentBackground w-[calc(100%_-_208px)] h-[calc(100%_-_74px)] p-9 overflow-auto">
        <div className="flex flex-col gap-4">
          {(!policiesLoading && !devicePolicies && !travelPolicies) ||
            (devicePolicies && devicePolicies.length === 0 && (
              <div className="w-[800px] h-56 px-8 py-6 bg-neutral-2 rounded-lg outline outline-1 outline-mainBorder inline-flex flex-col justify-end items-start gap-6 overflow-hidden mx-auto">
                <div className="w-6 h-6 relative overflow-hidden">
                  <IconListCheck size={24} className="absolute text-textTertiary" />
                </div>
                <div className="flex flex-col justify-center items-start gap-2">
                  <div className="self-stretch justify-center text-mainText font-semibold">
                    You have no device policies
                  </div>
                  <div className="w-[626px] justify-center text-textTertiary text-xs">
                    Set clear mobile security standards and prove compliance with powerful, flexible Policies. Start
                    with visibility to drive better hygiene, then turn on enforcement and integrations when you are
                    ready.
                  </div>
                </div>
                <div className="inline-flex justify-start items-start gap-2">
                  <Button
                    className="h-7 bg-mainPurple text-xs hover:bg-tertiaryPurple active:active:bg-tertiaryPurple cursor-pointer"
                    onClick={() => navigate('/create-device-policy')}
                  >
                    Create Policy
                  </Button>

                  <button
                    className="h-7 bg-white text-xs text-textTertiary border border-mainBorder rounded px-3 font-medium cursor-pointer"
                    onClick={() =>
                      window.open('https://edr.iverify.io/docs/iverify-portal-guide/device-policies/', '_blank')
                    }
                  >
                    Learn more
                  </button>
                </div>
              </div>
            ))}
          {!policiesLoading && combinedPolicies && combinedPolicies.length > 0 && (
            <>
              <DataGrid
                columns={policyColumns({
                  goToDevicePolicy,
                  renamePolicy,
                  deletePolicy,
                  duplicatePolicy,
                  isThreatLevelEnabled,
                })}
                data={combinedPolicies}
                loading={policiesLoading}
                classNames={inPov(session) ? 'max-h-[calc(100vh_-_276px)]' : 'max-h-[calc(100vh_-_240px)]'}
              />
              <div className="mt-2">
                {/* <Pagination
                totalPages={totalPages}
                forcePage={memberTableCurrentPage}
                handlePageClick={handlePageClick}
              /> */}
              </div>
            </>
          )}
          {policiesLoading && (
            <div className="flex items-center justify-center mt-32 animate-spin">
              {policiesLoading && <IconLoader2 size={64} />}
            </div>
          )}
        </div>
      </div>
      {showRenamePolicyModal && (
        <RenamePolicyModal
          setShowRenamePolicyModal={setShowRenamePolicyModal}
          policyToAction={policyToAction}
          setPolicyToAction={setPolicyToAction}
        />
      )}
      {showDeletePolicyModal && (
        <DeletePolicyModal
          setShowDeletePolicyModal={setShowDeletePolicyModal}
          policyToAction={policyToAction}
          setPolicyToAction={setPolicyToAction}
        />
      )}
      {showDuplicatePolicyModal && (
        <DuplicatePolicyModal
          setShowDuplicatePolicyModal={setShowDuplicatePolicyModal}
          policyToAction={policyToAction}
          setPolicyToAction={setPolicyToAction}
        />
      )}
    </>
  )
}

export default NewDevicePoliciesWrapper
